import {
  ExportService,
  ExportFileFormat,
  ExportContext,
  ExportRange,
} from "app/layout/export-overlay";
import { Injectable } from "@angular/core";
import { LiquidationAttributesReport, LiquidationDetail, LiquidationExportData, LiquidationQuantity, LiquidationUnitOfMeasure } from "app/modules/components/monitor/monitor-routes/interfaces/liquidation.interface";
import { LIQUIDATION_PROPERTIES } from "./liquidation-report.constants";
import { environment as env } from 'environments/environment.prod';
import { buildQuery, CombinedFilter, page, seed } from "@albatrosdeveloper/ave-utils-npm/lib/utils/query.util";
import { IPagination, IPaginationResult } from "app/common/interfaces";
import { LiquidationService } from "app/modules/components/monitor/monitor-routes/liquidation.service";
import { takeUntil } from "rxjs";
import { get } from "lodash-es";
import ceil from "lodash-es/ceil";
import { IFilters } from "app/modules/components/filters/interfaces/filterInterface";


interface Label {
    label: string;
    quantity: number;
    parallelQuantity: number;
}

interface Batch {
    quantity: number;
    parallelQuantity: number;
    bacth: string;
    labels: Label[];
}

interface BatchQuantities {
    batchesLoaded: Batch[];
    batchesDelivered: Batch[];
    batchesReturnedBE: Batch[];
    batchesReturnedME: Batch[];
}

interface LabelDetailRow {
    serial: string;
    trip: string;
    materialCode: string;
    materialName: string;

    batchCode: string;
    batchType: string;

    labelCode: string;
    unitBase: string;
    quantityBase: string;   
    unitParallel: string;
    quantityParallel: string;
}

export interface LiquidationRow {
  trip?: string | number;
  subRoute?: string;
  vehicleName?: string;
  serial?: string;
  username?: string;
  companyName?: string;
  salesOfficeName?: string;
  createdAt?: string | Date;

  materialCode?: string;
  materialName?: string;

  loadedBaseUnit?: string;
  loadedBase?: string;
  loadedParallelUnit?: string;
  loadedParallel?: string;

  deliveredBaseUnit?: string;
  deliveredBase?: string;
  deliveredParallelUnit?: string;
  deliveredParallel?: string;

  returnedBEBaseUnit?: string;
  returnedBEBase?: string;
  returnedBEParallelUnit?: string;
  returnedBEParallel?: string;

  returnedMEBaseUnit?: string;
  returnedMEBase?: string;
  returnedMEParallelUnit?: string;
  returnedMEParallel?: string;

  missingBaseUnit?: string;
  missingBase?: string;
  missingParallelUnit?: string;
  missingParallel?: string;
}


@Injectable()
export class LiquidationReportExport {
  constructor(
    private exportService: ExportService,
    private liquidationService: LiquidationService
  ) { }

  UMPACTIVE: boolean = false;

  private _getQuantityFields(
    quantityData: { quantity?: number, parallelQuantity?: number } | null,
    detail: LiquidationDetail,
    UMPACTIVE: boolean
  ): { quantityBase: string; unitBase: string; quantityParallel: string; unitParallel: string } {

    if (!quantityData) {
        return {
          unitBase: detail?.baseUnitOfMeasure?.name ?? '',
            quantityBase: '',  
            unitParallel: UMPACTIVE ? (detail?.pararellUnitOfMeasure?.name ?? '') : '',          
            quantityParallel: '',            
        };
    }

    const baseUnitName = detail?.baseUnitOfMeasure?.name ?? '';
    const parallelUnitName = detail?.pararellUnitOfMeasure?.name ?? '';

    const baseValue = quantityData.quantity ?? 0;
    const parallelValue = quantityData.parallelQuantity ?? 0;

    return {
        unitBase: baseUnitName,
        quantityBase: baseValue > 0 ? baseValue.toFixed(3) : '0.000',
        unitParallel: UMPACTIVE ? parallelUnitName : '',
        quantityParallel: UMPACTIVE ? (parallelValue > 0 ? parallelValue.toFixed(3) : '0.000') : '',        
    };
  }

  private _flattenDataForLabelSheet(liquidations, UMPACTIVE: boolean): LabelDetailRow[] {
    const flattenedLabels: LabelDetailRow[] = [];
    const batchKeys: Array<keyof BatchQuantities> = ['batchesLoaded', 'batchesDelivered', 'batchesReturnedBE', 'batchesReturnedME'];

    liquidations?.forEach((item) =>
      item?.detail?.forEach((detail) => {

        const materialContext: Omit<LabelDetailRow, 'batchCode' | 'batchType' | 'labelCode' | 'unitBase' | 'quantityBase' | 'unitParallel' | 'quantityParallel'> = {
            serial: get(item, "serial", ""),
            trip: get(item, "tripId", ""),
            materialCode: get(detail, "material.code", ""),
            materialName: get(detail, "material.name", ""),
        };

        batchKeys.forEach(key => {
            const batchTypeMapping = {
                batchesLoaded: 'Cargado',
                batchesDelivered: 'Entregado',
                batchesReturnedBE: 'Buen Estado',
                batchesReturnedME: 'Mal Estado',
            };
            const batchType = batchTypeMapping[key as keyof typeof batchTypeMapping] || key.replace('batches', '');

            const batches = get(detail.batches as BatchQuantities, key, []) as Batch[];

            batches.forEach((batch: Batch) => {
                const hasLabels = batch.labels && batch.labels.length > 0;

                if (hasLabels) {
                    batch.labels.forEach((label: Label) => {
                        const quantityFields = this._getQuantityFields(label, detail, UMPACTIVE);

                        const labelRow: LabelDetailRow = {
                            ...materialContext,
                            batchCode: batch.bacth,
                            batchType: batchType,
                            labelCode: label.label,
                            ...quantityFields
                        };
                        flattenedLabels.push(labelRow);
                    });
                }
                else {
                    const quantityDataForBatch: { quantity?: number, parallelQuantity?: number } = { 
                        quantity: batch.quantity, 
                        parallelQuantity: batch.parallelQuantity 
                    };
                    
                    const quantityFields = this._getQuantityFields(quantityDataForBatch, detail, UMPACTIVE); 
                    
                    const emptyLabelRow: LabelDetailRow = {
                        ...materialContext,
                        batchCode: batch.bacth,
                        batchType: batchType, 
                        labelCode: 'N/A (Sin Etiqueta)',
                        ...quantityFields
                    };
                    flattenedLabels.push(emptyLabelRow);
                }
            });
        });
      })
    );

    return flattenedLabels;
  }

  private transformData(liquidations: LiquidationAttributesReport[]): LiquidationRow[] {
    return liquidations?.flatMap((item: LiquidationAttributesReport) =>
      item?.detail?.flatMap((detail: LiquidationDetail) => {
        const rowData: LiquidationRow = {
          trip: item.tripId,
          subRoute: item.subRoute.name,
          vehicleName: item.vehicle.name,
          serial: item.serial,
          username: item.tripUser.user_login.username,
          companyName: item.company.name,
          salesOfficeName: item.salesOffice.name,
          createdAt: item.createdAt,
          materialCode: detail.material.code,
          materialName: detail.material.name,
          loadedBaseUnit: detail.baseUnitOfMeasure?.name ?? '',
          loadedBase: `${(detail.totalLoaded?.base ?? 0).toFixed(3)}`
        };

        if (this.UMPACTIVE) {
          rowData.loadedParallelUnit = detail.pararellUnitOfMeasure?.name ?? '';
          rowData.loadedParallel = `${(detail.totalLoaded?.pararell ?? 0).toFixed(3)}`;
        }

        rowData.deliveredBaseUnit = detail.baseUnitOfMeasure?.name ?? '';
        rowData.deliveredBase = `${(detail.totalDelivered?.base ?? 0).toFixed(3)}`;
        if (this.UMPACTIVE) {
          rowData.deliveredParallelUnit = detail.pararellUnitOfMeasure?.name ?? '';
          rowData.deliveredParallel = `${(detail.totalDelivered?.pararell ?? 0).toFixed(3)}`;
        }

        rowData.returnedBEBaseUnit = detail.baseUnitOfMeasure?.name ?? '';
        rowData.returnedBEBase = `${(detail.totalReturnedBE?.base ?? 0).toFixed(3)}`;
        if (this.UMPACTIVE) {
          rowData.returnedBEParallelUnit = detail.pararellUnitOfMeasure?.name ?? '';
          rowData.returnedBEParallel = `${(detail.totalReturnedBE?.pararell ?? 0).toFixed(3)}`;
        }

        rowData.returnedMEBaseUnit = detail.baseUnitOfMeasure?.name ?? '';
        rowData.returnedMEBase = `${(detail.totalReturnedME?.base ?? 0).toFixed(3)}`;
        if (this.UMPACTIVE) {
          rowData.returnedMEParallelUnit = detail.pararellUnitOfMeasure?.name ?? '';
          rowData.returnedMEParallel = `${(detail.totalReturnedME?.pararell ?? 0).toFixed(3)}`;
        }

        rowData.missingBaseUnit = detail.baseUnitOfMeasure?.name ?? '';
        rowData.missingBase = `${(detail.totalMissing?.base ?? 0).toFixed(3)}`;
        if (this.UMPACTIVE) {
          rowData.missingParallelUnit = detail.pararellUnitOfMeasure?.name ?? '';
          rowData.missingParallel = `${(detail.totalMissing?.pararell ?? 0).toFixed(3)}`;
        }

        return rowData;
      }) || []
    ) || [];
  }


  export(
    format: ExportFileFormat,
    range: ExportRange,
    totalItems: number,
    pageSize: number,
    pageIndex: number,
    exportId: string,
    exportTitle: string,
    properties: typeof LIQUIDATION_PROPERTIES,
    UMPACTIVE: boolean = false,
    filters: IFilters,
    optionsFilters: IPagination<LiquidationAttributesReport>
  ) {
    this.UMPACTIVE = UMPACTIVE;
    const totalPages = totalItems > pageSize ? ceil(totalItems / pageSize) + 1 : 1;

    const labelDetailProperties: Record<keyof LabelDetailRow, string> = {
        serial: 'No Doc (Liquidación)',
        trip: 'Viaje',
        materialCode: 'Cód. Producto',
        materialName: 'Producto',
        batchCode: 'Código de Lote',
        batchType: 'Tipo de Movimiento',
        labelCode: 'Etiqueta',
        unitBase: 'Unidad UMB',
        quantityBase: 'Cantidad UMB', 
        unitParallel: 'Unidad UMP',       
        quantityParallel: 'Cantidad UMP'        
    };
    if (!UMPACTIVE) {
        delete labelDetailProperties.quantityParallel;
        delete labelDetailProperties.unitParallel;
    }

    this.exportService
      .task({
        id: exportId,
        title: exportTitle,
        format,
        iterations: 1,
        start: (ctx: ExportContext) => {
          const dynamicProperties = this.UMPACTIVE ? {
            trip: 'Viaje',
            subRoute: "Ruta",
            vehicleName: 'Vehículo',
            serial: 'No Documento',
            username: 'Usuario',
            companyName: 'Compañía',
            salesOfficeName: 'Oficina de ventas',
            createdAt: 'Fecha de creación',
            materialCode: 'Código de material',
            materialName: 'Nombre de material',
            loadedBaseUnit: 'Unidad de medida Cargado UMB',
            loadedBase: 'Cargado UMB',
            loadedParallelUnit: 'Unidad de medida Cargado UMP',
            loadedParallel: 'Cargado UMP',
            deliveredBaseUnit: 'Unidad de medida Entregado/Facturado UMB',
            deliveredBase: 'Entregado/Facturado UMB',
            deliveredParallelUnit: 'Unidad de medida Entregado/Facturado UMP',
            deliveredParallel: 'Entregado/Facturado UMP',
            returnedBEBaseUnit: 'Unidad de medida Buen Estado UMB',
            returnedBEBase: 'Buen Estado UMB',
            returnedBEParallelUnit: 'Unidad de medida Buen Estado UMP',
            returnedBEParallel: 'Buen Estado UMP',
            returnedMEBaseUnit: 'Unidad de medida Mal Estado UMB',
            returnedMEBase: 'Mal Estado UMB',
            returnedMEParallelUnit: 'Unidad de medida Mal Estado UMP',
            returnedMEParallel: 'Mal Estado UMP',
            missingBaseUnit: 'Unidad de medida Faltante UMB',
            missingBase: 'Faltante UMB',
            missingParallelUnit: 'Unidad de medida Faltante UMP',
            missingParallel: 'Faltante UMP'
          } : {
            trip: 'Viaje',
            subRoute: "Ruta",
            vehicleName: 'Vehículo',
            serial: 'No Documento',
            username: 'Usuario',
            companyName: 'Compañía',
            salesOfficeName: 'Oficina de ventas',
            createdAt: 'Fecha de creación',
            materialCode: 'Código de material',
            materialName: 'Nombre de material',
            loadedBaseUnit: 'Unidad de medida Cargado UMB',
            loadedBase: 'Cargado UMB',
            deliveredBaseUnit: 'Unidad de medida Entregado/Facturado UMB',
            deliveredBase: 'Entregado/Facturado UMB',
            returnedBEBaseUnit: 'Unidad de medida Buen Estado UMB',
            returnedBEBase: 'Buen Estado UMB',
            returnedMEBaseUnit: 'Unidad de medida Mal Estado UMB',
            returnedMEBase: 'Mal Estado UMB',
            missingBaseUnit: 'Unidad de medida Faltante UMB',
            missingBase: 'Faltante UMB'
          };
          ctx.processor.localizeItemProperties(exportTitle, dynamicProperties);

          ctx.processor.localizeItemProperties('Detalle de Etiquetas', labelDetailProperties);
        },
        next: (ctx: ExportContext) => {
          optionsFilters.limitPerPage = range === ExportRange.AllPages ? totalItems : pageSize;
          optionsFilters.currentPage = range === ExportRange.AllPages ? 1 : optionsFilters.currentPage;
          this.liquidationService.getAllLiquidationsWithFilters(optionsFilters, filters)
            .pipe(takeUntil(ctx.stopped))
            .subscribe((response) => {
              const liquidations = response?.data?.data;

              const rows = this.transformData(liquidations);
              ctx.processor.addToSection(exportTitle, rows);

              const labelDetailData = this._flattenDataForLabelSheet(liquidations, this.UMPACTIVE);
              ctx.processor.addToSection('Detalle de Etiquetas', labelDetailData);

              ctx.next();
            });
        },
      })
      .start();
  }

  canStartTask(exportId: string): boolean {
    if (this.exportService.taskIsActive(exportId)) {
      this.exportService.setVisible(true);
      return false;
    }
    return true;
  }
}
